import React from 'react'

import { Box, Flex, Link, Text } from '@clear/design-system'
import PageContent from '@components/layout/PageContent'
import { HeadTitle } from '@components/layout/HeadTitle'
import DapScriptLoader from '@components/pages/shared/DapScriptLoader'

const TermsPage = () => {
  const linkArray = [
    {
      href: '#disclosures-and-terms',
      text: 'Disclosures and Terms',
    },
    {
      href: '#governmental-discosures',
      text: 'Governmental Disclosures',
    },
    {
      href: '#enrollment-and-terms-of-membership',
      text: 'Enrollment and Terms of Membership',
    },
    {
      href: '#charges-and-payment',
      text: 'Charges & Payment',
    },
    {
      href: '#contractors',
      text: 'Contractors',
    },
    {
      href: '#intellectual-property',
      text: 'Intellectual Property Rights; Restrictions on Use',
    },
    {
      href: '#termination',
      text: 'Termination',
    },
    {
      href: '#liability-related-to-tsa',
      text: 'Liability Related to TSA PreCheck® Enrollment',
    },
    {
      href: '#dispute-resolution',
      text: 'Dispute Resolution by Binding Arbitration',
    },
    {
      href: '#consent-and-written-release',
      text: 'Consent and Written Release for Collection, Use, Disclosure and Storage of Biometric Information and Identifiers',
    },
  ].map(({ href, text }: { href: string; text: string }) => (
    <Text
      as="a"
      href={href}
      key={href}
      className="hover:text-dhs-blue"
      display="block"
      mb={5}
    >
      {text}
    </Text>
  ))

  return (
    <Flex flexDirection="column">
      <DapScriptLoader />
      <HeadTitle
        title="Terms &amp; Conditions | TSA&nbsp;PreCheck&reg; Enrollment Provided by CLEAR"
        description="Find important terms and conditions for the TSA&nbsp;PreCheck&reg; Enrollment Provided by CLEAR. Learn more."
      />
      <PageContent>
        <Flex justifyContent="space-between" mx="auto" maxWidth="content">
          <Box
            display={['none', 'block']}
            flex="1 1 30%"
            mr={6}
            color="neutralsTextGray"
          >
            {linkArray}
          </Box>
          <Box flex="1 1 70%">
            <Text
              as="h1"
              fontSize={4}
              color="brandDarkBlue"
              mb={5}
              data-cy="header"
              id="disclosures-and-terms"
            >
              TSA&nbsp;PreCheck® Enrollment Provided by CLEAR: Disclosures and
              Terms
            </Text>
            <Text as="p" mb={4}>
              Thank you for enrolling in TSA&nbsp;PreCheck® Enrollment Provided
              by CLEAR, a private company under contract with TSA. Below are
              some required government disclosures we are sharing on behalf of
              the Department of Homeland Security (DHS) describing how your data
              is used and shared as your ongoing eligibility to participate in
              the TSA&nbsp;PreCheck® program is evaluated. Following these
              disclosures are some additional Terms and Conditions that govern
              your enrollment in TSA&nbsp;PreCheck® through this enrollment
              site. If you decide to enroll in CLEAR in addition to
              TSA&nbsp;PreCheck®, CLEAR’s member terms,&nbsp;
              <Link
                variant="regular"
                color="brandDarkBlue"
                href="https://www.clearme.com/member-terms"
                target="_blank"
              >
                https://www.clearme.com/member-terms
              </Link>
              , will also apply.
            </Text>
            <Text
              as="h2"
              fontSize={4}
              color="brandDarkBlue"
              mb={4}
              data-cy="header"
              id="governmental-discosures"
            >
              Governmental Disclosures
            </Text>
            <Text as="h2" mb={4}>
              PRIVACY ACT STATEMENT
            </Text>
            <Text as="p" mb={4}>
              <Text color="brandDarkBlue">Authority:</Text> 6 U.S.C. § 1140, 46
              U.S.C. § 70105; 49 U.S.C. §§ 106, 114, 5103a, 40103(b)(3), 40113,
              44903, 44935-44936, 44939, and 46105; the Implementing
              Recommendations of the 9/11 Commission Act of 2007, § 1520 (121
              Stat. 444, Public Law 110-52, August 3, 2007); and Executive Order
              9397, as amended.
            </Text>
            <Text as="p" mb={4}>
              <Text color="brandDarkBlue">Purpose:</Text> The Department of
              Homeland Security (DHS) will use your information to conduct a
              security threat assessment. Biometrics collected from applicants
              to the TSA&nbsp;PreCheck® Application Program may also be used to
              conduct screening at airport checkpoints. Your fingerprints and
              associated information will be provided to the Federal Bureau of
              Investigation (FBI) for the purpose of comparing your fingerprints
              to other fingerprints in the FBI’s Next Generation Identification
              (NGI) system or its successor systems including civil, criminal,
              and latent fingerprint repositories. The FBI may retain your
              fingerprints and associated information in NGI after the
              completion of this application and, while retained, your
              fingerprints may continue to be compared against other
              fingerprints submitted to or retained by NGI. DHS will also
              transmit your fingerprints for enrollment into Automated
              Biometrics Identification System (IDENT). If you provide your
              Social Security Number (SSN), DHS may provide your name and SSN to
              the Social Security Administration (SSA) to compare that
              information against SSA records to ensure the validity of the
              information.
            </Text>
            <Text as="p" mb={4}>
              <Text color="brandDarkBlue">Routine Uses:</Text> This system may
              disclose information in accordance with the Privacy Act, 5 U.S.C.
              552a(b), including as a routine use pursuant to 5 U.S.C.
              552a(b)(3) with third parties during the course of a security
              threat assessment, employment investigation, or adjudication of a
              waiver or appeal request to the extent necessary to obtain
              information pertinent to the assessment, investigation, or
              adjudication of your application under the TSA system of records
              notice (SORN) DHS/TSA 021, TSA&nbsp;PreCheck® Application Program
              for applicants to that program. Disqualifying criminal offenses
              uncovered during your application may limit your ability to access
              TSA&nbsp;PreCheck® expedited screening. For as long as your
              fingerprints and associated information are retained in NGI, your
              information may be disclosed pursuant to your consent or without
              your consent as permitted by the Privacy Act of 1974 and all
              applicable Routine Uses as may be published at any time in the
              Federal Register, including the Routine Uses for the NGI system
              and the FBI’s Blanket Routine Uses.
            </Text>
            <Text as="p" mb={4}>
              <Text color="brandDarkBlue">Disclosure:</Text> Furnishing this
              information (including your SSN) is voluntary; however, if you do
              not provide your SSN or any other information requested, DHS may
              be unable to complete your application for a security threat
              assessment.
            </Text>
            <Text as="h2" mb={4}>
              PAPERWORK REDUCTION ACT STATEMENT
            </Text>
            <Text as="p" mb={4}>
              <Text color="brandDarkBlue">Statement of Public Burden:</Text>{' '}
              This is a voluntary collection of information, but failure to
              provide the information may result in an inability to approve your
              eligibility for the requested TSA program or benefit. TSA
              estimates that the total average burden per response associated
              with this collection for enrollment is approximately 30 minutes.
              An agency may not conduct or sponsor, and a person is not required
              to respond to, a collection of information unless it displays a
              valid OMB control number. The control number for this collection
              is OMB 1652-0059, which expires 03/31/2028.
            </Text>
            <Text as="p" mb={4}>
              <Text color="brandDarkBlue">
                CLEAR Terms and Conditions for TSA&nbsp;PreCheck® Enrollment
              </Text>
              <br />
              By enrolling in TSA&nbsp;PreCheck® you acknowledge the disclosures
              set forth above. By enrolling in TSA&nbsp;PreCheck® Enrollment
              Provided by CLEAR you agree to all the terms and conditions set
              forth below (the “Terms”). If you do not agree to these Terms,
              please do not enroll in TSA&nbsp;PreCheck® Enrollment Provided by
              CLEAR and instead use other available options for enrolling in
              TSA&nbsp;PreCheck® found on{' '}
              <Link
                variant="regular"
                color="brandDarkBlue"
                href="https://www.tsa.gov/precheck"
                target="_blank"
              >
                tsa.gov/precheck
              </Link>
              . This TSA&nbsp;PreCheck® enrollment opportunity is made available
              to you by Alclear PC, LLC and its affiliates.{' '}
            </Text>
            <Text as="p" mb={4}>
              PLEASE READ THESE TERMS CAREFULLY. THEY CONTAIN AN ARBITRATION
              AGREEMENT THAT APPLIES TO YOUR RELATIONSHIP WITH CLEAR BUT NOT TO
              YOUR RELATIONSHIP WITH TSA OR THE U.S. GOVERNMENT, AND IMPORTANT
              INFORMATION REGARDING YOUR LEGAL RIGHTS AND REMEDIES. THE
              ARBITRATION AGREEMENT REQUIRES (WITH LIMITED EXCEPTION) THAT YOU
              SUBMIT CLAIMS AGAINST CLEAR TO BINDING ARBITRATION, AND (1) YOU
              MAY ONLY PURSUE CLAIMS ON AN INDIVIDUAL BASIS, NOT IN ANY CLASS OR
              REPRESENTATIVE ACTION, (2) YOU MAY ONLY SEEK RELIEF ON AN
              INDIVIDUAL BASIS, AND (3) YOU MAY NOT SEEK RESOLUTION OF ANY
              CLAIMS BY A JURY OR IN A COURT OF LAW.
            </Text>
            <Text as="p" mb={4}>
              AT THE CONCLUSION OF YOUR TSA&nbsp;PRECHECK® ENROLLMENT
              APPLICATION YOU WILL BE PROVIDED THE OPPORTUNITY TO ENROLL IN
              CLEAR AS WELL. YOUR TSA&nbsp;PRECHECK® ENROLLMENT AND ELIGIBILITY
              WILL IN NO WAY BE IMPACTED BY WHETHER YOU ELECT TO ENROLL IN
              CLEAR. IN THE EVENT YOU DECIDE TO ENROLL IN CLEAR, CLEAR’S MEMBER
              TERMS,&nbsp;
              <Link
                variant="regular"
                color="brandDarkBlue"
                href="https://www.clearme.com/member-terms"
                target="_blank"
              >
                HTTPS://WWW.CLEARME.COM/MEMBER-TERMS
              </Link>
              , WILL APPLY.
            </Text>
            <Text
              as="h2"
              fontSize={4}
              color="brandDarkBlue"
              mb={4}
              id="enrollment-and-terms-of-membership"
            >
              1. Enrollment and Terms of Membership
            </Text>
            <Text as="h3">
              <Text mr={2}>1.1</Text>
              <span>Enrollment</span>
            </Text>
            <Text as="p" mb={4}>
              To use TSA&nbsp;PreCheck®, you must complete your enrollment, be
              deemed eligible, receive a Known Traveler Number and become a
              “Member”. You may enroll either (a) through this website followed
              by an in-person visit to a TSA&nbsp;PreCheck® Enrollment Provided
              by CLEAR enrollment location or (b) at a TSA&nbsp;PreCheck®
              Enrollment Provided by CLEAR enrollment location without an online
              pre-enrollment. To begin enrollment, you must meet the
              qualifications set forth here. By enrolling in TSA&nbsp;PreCheck®
              Enrollment Provided by CLEAR you hereby expressly consent to
              CLEAR’s sharing of your biographic and biometric information with
              TSA, the FBI and other governmental agencies for the purpose of
              determining your eligibility and administration of the
              TSA&nbsp;PreCheck® program. Please see the Privacy Act Statement
              above for further details. If your application is not approved by
              TSA, you will not be entitled to a refund of your enrollment fees.
              TSA&nbsp;PreCheck® is a program administered solely by TSA and may
              be subject to change as promulgated by an authorized government
              agency. Initiating or completing enrollment in TSA PreCheck® does
              not guarantee an enrollee’s continued opportunity to receive
              TSA&nbsp;PreCheck® expedited screening.
            </Text>
            <Text as="h3">
              <Text mr={2}>1.2</Text>
              <span>Term of Membership and Fees / Background Checks</span>
            </Text>
            <Text as="p" mb={4}>
              You may enroll in TSA&nbsp;PreCheck® for a five-year initial term
              (the “Initial Term”). Your initial TSA risk assessment as
              described above in the Privacy Act statement shall be valid for a
              five year period, unless TSA determines you are no longer
              eligible. The Initial Term and any following renewal terms are
              collectively referred to as the “Term”).
            </Text>
            <Text as="h3">
              <Text mr={2}>1.3</Text>
              <span>Notices</span>
            </Text>
            <Text as="p" mb={4}>
              All notices from CLEAR related to your TSA&nbsp;PreCheck®
              enrollment or renewal to you are considered received and effective
              when we send them to the email address you give us when you enroll
              or later update, or via text message if you have enrolled in SMS
              or MMS notifications, or as otherwise set forth herein (“Notice”).
              If you would like to update your information, please call{' '}
              <Link
                variant="regular"
                color="brandDarkBlue"
                href="tel:1-888-831-9070"
              >
                1-888-831-9070
              </Link>{' '}
              or email{' '}
              <Link
                variant="regular"
                color="brandDarkBlue"
                href="mailto:clearcustomersupport@tsa.dhs.gov"
                target="_blank"
              >
                CLEARCustomerSupport@tsa.dhs.gov
              </Link>
            </Text>
            <Text
              as="h2"
              fontSize={4}
              color="brandDarkBlue"
              mb={4}
              id="charges-and-payment"
            >
              2. Charges &amp; Payment
            </Text>
            <Text as="p" mb={4}>
              You agree to pay the fees set forth when you sign up and in these
              Terms (collectively, “Charges”). The fees include all required
              governmental fees related to your Initial Term. You agree that
              Charges for any renewal term will be as set forth at the time of
              your renewal, for which we will provide you with Notice. You
              authorize us to bill Charges to your credit or debit card, or
              agree to pay by another payment method approved by us, as
              specified during your enrollment process. You agree to pay the
              cost of any returned check or other fees charged to us as a result
              of your insufficient funds.
            </Text>
            <Text
              as="h2"
              fontSize={4}
              color="brandDarkBlue"
              mb={4}
              id="contractors"
            >
              3. Contractors
            </Text>
            <Text as="p" mb={4}>
              CLEAR may use third parties to help perform your
              TSA&nbsp;PreCheck® enrollment.
            </Text>
            <Text
              as="h2"
              fontSize={4}
              color="brandDarkBlue"
              mb={4}
              id="intellectual-property"
            >
              4. Intellectual Property Rights; Restrictions on Use
            </Text>
            <Text as="h3">
              <Text mr={2}>4.1</Text>
              <span>Ownership of Platform and Related Data</span>
            </Text>
            <Text as="p" mb={4}>
              As between CLEAR and Members (collectively, “Parties”), CLEAR owns
              all rights, title and interest in and to the enrollment platform,
              including (1) any derivative works; and (2) any intellectual
              property or other proprietary rights related to the foregoing
              (collectively, “CLEAR IP”). Nothing in these Terms grants any
              license to you or other Members under any CLEAR IP. TSA reserves
              all rights related to the TSA&nbsp;PreCheck® program, including
              the TSA PreCheck® trademark, which we use under a license
              agreement between TSA and CLEAR.
            </Text>
            <Text
              as="h2"
              fontSize={4}
              color="brandDarkBlue"
              mb={4}
              id="termination"
            >
              5. Termination
            </Text>
            <Text as="p" mb={4}>
              Notwithstanding the termination of your membership in the TSA
              PreCheck® Program, the following sections of the Terms shall
              survive any such termination: Sections 2 (to the extent any fees
              remain due and payable), 4.1, 6 and 7–9 (inclusive). Upon the
              termination of your membership, all rights and obligations of the
              Parties under these Terms shall expire, except those specifically
              designated in this Section 5.
            </Text>
            <Text
              as="h2"
              fontSize={4}
              color="brandDarkBlue"
              mb={4}
              id="liability-related-to-tsa"
            >
              6. Liability Related to TSA&nbsp;PreCheck® Enrollment
            </Text>
            <Text as="h3">
              <Text mr={2}>6.1</Text>
              <span>Limitation of Liability</span>
            </Text>
            <Text as="p" mb={4}>
              THE LIABILITY TO YOU OF CLEAR OR CLEAR’S AFFILIATES (INCLUDING
              CLEAR’S HOLDING COMPANY), OR THEIR RESPECTIVE CONTRACTORS OR
              CURRENT, FUTURE OR FORMER OFFICERS, DIRECTORS, EQUITY OR UNIT
              HOLDERS, EMPLOYEES, AGENTS, SUCCESSORS OR ASSIGNS (COLLECTIVELY,
              THE “CLEAR GROUP”), ARISING OUT OF OR RESULTING FROM THE
              PERFORMANCE OR NON-PERFORMANCE BY THE CLEAR GROUP OF THE PROGRAM
              AND ITS OBLIGATIONS UNDER THESE TERMS OR RELATED TO THE TSA
              PRECHECK® PROGRAM GENERALLY SHALL BE LIMITED TO ACTUAL, DIRECT
              DAMAGES INCURRED BY YOU FOR EACH EVENT THAT IS THE SUBJECT MATTER
              OF A CLAIM OR CAUSE OF ACTION. IN SUCH AN EVENT, THE AGGREGATE
              LIABILITY OF THE CLEAR GROUP FOR DIRECT DAMAGES FOR ALL SUCH
              CLAIMS SHALL NOT EXCEED THE AMOUNT PAID BY MEMBER TO CLEAR DURING
              THE TWELVE (12) MONTHS IMMEDIATELY PRECEDING SUCH CLAIM. THE CLEAR
              GROUP SHALL NOT, IN ANY EVENT, BE LIABLE FOR DAMAGES THAT
              CONSTITUTE (1) LOSS OF INTEREST, PROFIT OR REVENUE OF THE CLAIMING
              PARTY, OR (2) INCIDENTAL, CONSEQUENTIAL, SPECIAL, PUNITIVE,
              EXEMPLARY, MULTIPLE OR INDIRECT DAMAGES SUFFERED BY THE CLAIMING
              PARTY, EVEN IF ANY MEMBER OF THE CLEAR GROUP HAS BEEN ADVISED OF
              THE POSSIBILITY OF SUCH LOSSES OR DAMAGES. IF YOU ARE A USER FROM
              NEW JERSEY, THE FOREGOING SECTIONS TITLED “DISCLAIMERS” AND
              “LIMITATION OF LIABILITY”, AND THE BELOW SECTION “INDEMNITIES” ARE
              INTENDED TO BE ONLY AS BROAD AS IS PERMITTED UNDER THE LAWS OF THE
              STATE OF NEW JERSEY. IF ANY PORTION OF THESE SECTIONS IS HELD TO
              BE INVALID UNDER THE LAWS OF THE STATE OF NEW JERSEY, THE
              INVALIDITY OF SUCH PORTION SHALL NOT AFFECT THE VALIDITY OF THE
              REMAINING PORTIONS OF THE APPLICABLE SECTIONS.
            </Text>
            <Text as="h3">
              <Text mr={2}>6.2</Text>
              <span>Remedies</span>
            </Text>
            <Text as="p" mb={4}>
              At its option, any member of the CLEAR Group may seek all remedies
              available to it under law and in equity, including injunctive
              relief in the form of specific performance to enforce these Terms
              and actions for damages.
            </Text>
            <Text
              as="h2"
              fontSize={4}
              color="brandDarkBlue"
              mb={4}
              id="dispute-resolution"
            >
              7. Dispute Resolution by Binding Arbitration
            </Text>
            <Text as="p" mb={4}>
              PLEASE READ THIS SECTION CAREFULLY AS IT AFFECTS YOUR RIGHTS.
              PLEASE NOTE THAT THESE ARBITRATION TERMS ARE SPECIFIC TO CLEAR,
              NOT TSA. IF YOU DO NOT WISH TO ACCEPT THESE TERMS, YOU MAY SELECT
              AN ALTERNATIVE ENROLLMENT PARTNER, AVAILABLE AT{' '}
              <Link
                variant="regular"
                color="brandDarkBlue"
                href="https://www.tsa.gov/precheck"
                target="_blank"
              >
                TSA.GOV/PRECHECK
              </Link>
              .
            </Text>
            <Text as="h3">
              <Text mr={2}>7.1</Text>
              <span>Agreement to Arbitrate</span>
            </Text>
            <Text as="p" mb={4}>
              This Dispute Resolution by Binding Arbitration section is referred
              to in these Terms as the “Arbitration Agreement.” You agree that
              any and all disputes or claims that have arisen or may arise
              between yourself and CLEAR, whether arising out of or relating to
              these Terms (including any alleged breach thereof), any
              advertising, any aspect of the relationship or transactions
              between us, shall be resolved exclusively through final and
              binding arbitration, rather than a court, in accordance with the
              terms of this Arbitration Agreement, except that you may assert
              individual claims in small claims court, if your claims qualify.
              Further, this Arbitration Agreement does not preclude you from
              bringing issues to the attention of federal, state, or local
              agencies, and such agencies can, if the law allows, seek relief
              against us on your behalf. You agree that, by entering into these
              Terms, you and CLEAR are each waiving the right to a trial by jury
              or to participate in a class action. Your rights will be
              determined by a neutral arbitrator, not a judge or jury. The
              Federal Arbitration Act governs the interpretation and enforcement
              of this Arbitration Agreement. For clarity, this Section 7.1 shall
              not apply to any disputes or claims between yourself and TSA
              concerning these Terms or the TSA&nbsp;PreCheck® service.
            </Text>
            <Text as="h3">
              <Text mr={2}>7.2</Text>
              <span>
                Prohibition of Class and Representative Actions and
                Non-Individualized Relief
              </span>
            </Text>
            <Text as="p" mb={4}>
              YOU AND CLEAR AGREE THAT EACH OF US MAY BRING CLAIMS AGAINST THE
              OTHER ONLY ON AN INDIVIDUAL BASIS AND NOT AS A PLAINTIFF OR CLASS
              MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE ACTION OR
              PROCEEDING. UNLESS BOTH YOU AND CLEAR AGREE OTHERWISE, THE
              ARBITRATOR MAY NOT CONSOLIDATE OR JOIN MORE THAN ONE PERSON’S OR
              PARTY’S CLAIMS AND MAY NOT OTHERWISE PRESIDE OVER ANY FORM OF A
              CONSOLIDATED, REPRESENTATIVE, OR CLASS PROCEEDING. ALSO, THE
              ARBITRATOR MAY AWARD RELIEF (INCLUDING MONETARY, INJUNCTIVE, AND
              DECLARATORY RELIEF) ONLY IN FAVOR OF THE INDIVIDUAL PARTY SEEKING
              RELIEF AND ONLY TO THE EXTENT NECESSARY TO PROVIDE RELIEF
              NECESSITATED BY THAT PARTY’S INDIVIDUAL CLAIM(S), EXCEPT THAT YOU
              MAY PURSUE A CLAIM FOR AND THE ARBITRATOR MAY AWARD PUBLIC
              INJUNCTIVE RELIEF UNDER APPLICABLE LAW TO THE EXTENT REQUIRED FOR
              THE ENFORCEABILITY OF THIS PROVISION.
            </Text>
            <Text as="h3">
              <Text mr={2}>7.3</Text>
              <span>Pre-Arbitration Dispute Resolution</span>
            </Text>
            <Text as="p" mb={4}>
              CLEAR is always interested in resolving disputes amicably and
              efficiently, and most customer concerns can be resolved quickly
              and to the customer’s satisfaction by emailing customer support
              at, MEMBERSERVICES@CLEARME.COM. If such efforts prove
              unsuccessful, a party who intends to seek arbitration must first
              send to the other, by certified mail, a written Notice of Dispute
              (“Arbitration Notice”). The Arbitration Notice to CLEAR should be
              sent to CLEAR at 85 10th Ave, Floor 10, New York, New York 10011,
              Attn: Legal (“Arbitration Notice Address”). The Arbitration Notice
              must (i) describe the nature and basis of the claim or dispute and
              (ii) set forth the specific relief sought. If CLEAR and you do not
              resolve the claim within sixty (60) calendar days after the
              Arbitration Notice is received, you or CLEAR may commence an
              arbitration proceeding. During the arbitration, the amount of any
              settlement offer made by CLEAR or you shall not be disclosed to
              the arbitrator until after the arbitrator determines the amount,
              if any, to which you or CLEAR is entitled.
            </Text>
            <Text as="h3">
              <Text mr={2}>7.4</Text>
              <span>Arbitration Procedures</span>
            </Text>
            <Text as="p" mb={4}>
              Arbitration will be conducted by a neutral arbitrator in
              accordance with the American Arbitration Association’s (“AAA”)
              rules and procedures, including the AAA’s Consumer Arbitration
              Rules (collectively, the “AAA Rules”), as modified by this
              Arbitration Agreement. For information on the AAA, please visit
              its website,{' '}
              <Link
                variant="regular"
                color="brandDarkBlue"
                href="HTTPS://WWW.ADR.ORG"
                target="_blank"
              >
                HTTPS://WWW.ADR.ORG
              </Link>
              . Information about the AAA Rules and fees for consumer disputes
              can be found at the AAA’s consumer arbitration page,{' '}
              <Link
                variant="regular"
                color="brandDarkBlue"
                href="HTTPS://WWW.ADR.ORG/CONSUMER"
                target="_blank"
              >
                HTTPS://WWW.ADR.ORG/CONSUMER
              </Link>
              . CLEAR may also choose to conduct the arbitration before a
              three-member panel of neutral arbitrators with CLEAR bearing all
              costs of arbitration and in accordance with the JAMS Comprehensive
              Arbitration Rules &amp; Procedures (collectively, the “JAMS
              Rules”), as modified by this Arbitration Agreement. For
              information on JAMS, please visit its website, its website,{' '}
              <Link
                variant="regular"
                color="brandDarkBlue"
                href="HTTPS://WWW.JAMSADR.COM"
                target="_blank"
              >
                HTTPS://WWW.JAMSADR.COM
              </Link>{' '}
              Information about the JAMS Rules and fees for consumer disputes
              can be found at the JAMS’ arbitration page,{' '}
              <Link
                variant="regular"
                color="brandDarkBlue"
                href="HTTPS://WWW.JAMSADR.COM/CONSUMER-MINIMUM-STANDARDS/"
                target="_blank"
              >
                HTTPS://WWW.JAMSADR.COM/CONSUMER-MINIMUM-STANDARDS/
              </Link>
              . If there is any inconsistency between any term of the AAA Rules
              or JAMS Rules and any term of this Arbitration Agreement, the
              applicable terms of this Arbitration Agreement will control unless
              the arbitrator determines that the application of the inconsistent
              Agreement terms would not result in a fundamentally fair
              arbitration. The arbitrator must also follow the provisions of
              these Terms as a court would. All issues are for the arbitrator to
              decide, including, but not limited to, issues relating to the
              scope, enforceability, and arbitrability of this Arbitration
              Agreement. Although arbitration proceedings are usually simpler
              and more streamlined than trials and other judicial proceedings,
              the arbitrator can award the same damages and relief on an
              individual basis that a court can award to an individual under the
              Terms and applicable law. Decisions by the arbitrator are
              enforceable in court and may be overturned by a court only for
              very limited reasons. Unless CLEAR and Member agree otherwise, any
              arbitration hearings will take place in a reasonably convenient
              location for both parties with due consideration of their ability
              to travel and other pertinent circumstances. If the parties are
              unable to agree on a location, the determination shall be made by
              AAA or JAMS. If Member’s claim is for $10,000 or less, CLEAR
              agrees that Member may choose whether the arbitration will be
              conducted solely on the basis of documents submitted to the
              arbitrator, through a telephonic hearing, or by an in-person
              hearing as established by the AAA Rules or JAMS Rules. If Member’s
              claim exceeds $10,000, the right to a hearing will be determined
              by the AAA Rules or JAMS Rules. Regardless of the manner in which
              the arbitration is conducted, the arbitrator shall issue a
              reasoned written decision sufficient to explain the essential
              findings and conclusions on which the award is based.
            </Text>
            <Text as="h3">
              <Text mr={2}>7.5</Text>
              <span>Costs of Arbitration</span>
            </Text>
            <Text as="p" mb={4}>
              Payment of all filing, administration, and arbitrator fees
              (collectively, the “Arbitration Fees”) will be governed by the AAA
              Rules, unless otherwise provided in this Arbitration Agreement. If
              Member is able to demonstrate to the arbitrator that he or she is
              economically unable to pay their portion of the Arbitration Fees
              or if the arbitrator otherwise determines for any reason that
              Member should not be required to pay his or her portion of the
              Arbitration Fees, CLEAR will pay Member’s portion of such fees. In
              addition, if Member demonstrates to the arbitrator that the costs
              of arbitration will be prohibitive as compared to the costs of
              litigation, CLEAR will pay as much of the Arbitration Fees as the
              arbitrator deems necessary to prevent the arbitration from being
              cost-prohibitive. CLEAR will bear all Arbitration Fees if it
              elects to use JAMS. Any payment of attorneys’ fees will be
              governed by the AAA Rules or JAMS Rules.
            </Text>
            <Text as="h3">
              <Text mr={2}>7.6</Text>
              <span>Confidentiality</span>
            </Text>
            <Text as="p" mb={4}>
              All aspects of the arbitration proceeding, and any ruling,
              decision, or award by the arbitrator, will be strictly
              confidential for the benefit of all parties.
            </Text>
            <Text as="h3">
              <Text mr={2}>7.7</Text>
              <span>Severability</span>
            </Text>
            <Text as="p" mb={4}>
              If a court or the arbitrator decides that any term or provision of
              this Arbitration Agreement (other than the subsection (b) titled
              “Prohibition of Class and Representative Actions and
              Non-Individualized Relief” above) is invalid or unenforceable, the
              parties agree to replace such term or provision with a term or
              provision that is valid and enforceable and that comes closest to
              expressing the intention of the invalid or unenforceable term or
              provision, and this Arbitration Agreement shall be enforceable as
              so modified. If a court or the arbitrator decides that any of the
              provisions of subsection (b) above titled “Prohibition of Class
              and Representative Actions and Non-Individualized Relief” are
              invalid or unenforceable, then the entirety of this Arbitration
              Agreement shall be null and void, unless such provisions are
              deemed to be invalid or unenforceable solely with respect to
              claims for public injunctive relief. The remainder of the Terms
              will continue to apply.
            </Text>
            <Text as="h3">
              <Text mr={2}>7.8</Text>
              <span>Future Changes to Arbitration Agreement</span>
            </Text>
            <Text as="p" mb={4}>
              Notwithstanding any provision in these Terms to the contrary,
              CLEAR agrees that if it makes any future change to this
              Arbitration Agreement (other than a change to the Arbitration
              Notice Address) while you are a Member, you may reject any such
              change by sending CLEAR written notice within thirty (30) calendar
              days of the change to the Arbitration Notice Address provided
              above. By rejecting any future change, you are agreeing that you
              will arbitrate any dispute between us in accordance with the
              language of this Arbitration Agreement as of the date you first
              accepted these Terms (or accepted any subsequent changes to these
              Terms).
            </Text>
            <Text as="h2" fontSize={4} color="brandDarkBlue" mb={4}>
              8. General
            </Text>
            <Text as="h3">
              <Text mr={2}>8.1</Text>
              <span>Taxes</span>
            </Text>
            <Text as="p" mb={4}>
              You shall be responsible for federal, state, local and foreign
              taxes imposed on any consideration for any provision of services
              (including, without limitation, TSA&nbsp;PreCheck®) or transfer of
              property by CLEAR to you.
            </Text>
            <Text as="h3">
              <Text mr={2}>8.2</Text>
              <span>Entire Agreement, Amendments and Modifications</span>
            </Text>
            <Text as="p" mb={4}>
              These Terms constitute the entire agreement of the Parties with
              regard to the Program and all matters addressed herein, and all
              prior agreements, letters, proposals, discussions and other
              documents regarding the Program and the matters herein are
              superseded and merged into these Terms. In the event of any
              conflict between these Terms and any Additional Terms, these Terms
              control.
            </Text>
            <Text as="h3">
              <Text mr={2}>8.3</Text>
              <span>Force Majeure</span>
            </Text>
            <Text as="p" mb={4}>
              CLEAR will be excused from performance under these Terms for any
              period we are prevented from or delayed in performing any
              obligations pursuant to these Terms, in whole or in part, as a
              result of a Force Majeure Event. For purposes of this Section 8.3,
              “Force Majeure Event” means an event or series of events caused by
              or resulting from any of the following: (1) weather conditions or
              other elements of nature or acts of God; (2) acts of war, acts of
              terrorism, insurrection, riots, civil disorders or rebellion; (3)
              quarantines or embargoes, (4) labor strikes; (5) laws or
              regulation that change the nature of a Program or Programs or (6)
              other causes beyond the reasonable control of CLEAR.
            </Text>
            <Text as="h3">
              <Text mr={2}>8.4</Text>
              <span>Severability</span>
            </Text>
            <Text as="p" mb={4}>
              If any provision of these Terms is held invalid, illegal or
              unenforceable, the validity, legality and enforceability of the
              remaining provisions shall not be affected or impaired, and such
              provision shall be deemed to be restated to reflect the Parties’
              original intentions as nearly as possible in accordance with
              applicable law.
            </Text>
            <Text as="h3">
              <Text mr={2}>8.5</Text>
              <span>Governing Law</span>
            </Text>
            <Text as="p" mb={4}>
              These Terms will be governed by the laws of the State of New York
              without regard to its conflict of law provisions. With respect to
              any disputes or claims not subject to arbitration, as set forth
              above, you and CLEAR agree to submit to the personal and exclusive
              jurisdiction of the state and federal courts located within New
              York, New York.
            </Text>
            <Text as="h3">
              <Text mr={2}>8.6</Text>
              <span>Assignment</span>
            </Text>
            <Text as="p" mb={4}>
              You may not assign these Terms (or any rights, benefits or
              obligations hereunder) by operation of law or otherwise without
              our prior written consent, which we may withhold in our sole
              discretion. Any attempted assignment by you that does not comply
              with the terms of this Section 8.6 shall be null and void. CLEAR
              may assign these Terms, in whole or in part, to any third party in
              its sole discretion.
            </Text>
            <Text>
              <Text mr={2}>8.7</Text>
              <span>SMS and Text Messaging</span>
            </Text>
            <Text as="p" mb={4}>
              We may provide the option for you to request customer service via
              text messaging. You agree that by initiating communications with
              us by text message (including SMS and MMS messages), you consent
              to and authorize us to provide service to you and/or to respond to
              your communications by text messaging you, including by using an
              automatic telephone dialing system.
            </Text>

            <Text as="p" mb={4}>
              When you opt-in to the service via CLEAR’s website, you consent
              and agree that we may send you a message to confirm your signup.{' '}
              <Text fontWeight="900">Message and data rates may apply.</Text>{' '}
              Text <Text fontWeight="900">"HELP"</Text> for help. Text{' '}
              <Text fontWeight="900">"STOP"</Text> to cancel.
            </Text>
            <Text as="p" mb={4}>
              You can cancel this service at any time. Just text{' '}
              <Text fontWeight="900">"STOP"</Text> to{' '}
              <Text fontWeight="900">72178</Text>. After you send the message{' '}
              <Text fontWeight="900">"STOP"</Text> to us, you consent and agree
              that we may send you a reply message to confirm that you have been
              unsubscribed. After this, you will no longer receive messages from
              us, unless you later opt in to or request the service.
            </Text>
            <Text as="p" mb={4}>
              Participating carriers include AT&T, Verizon Wireless, Sprint,
              T-Mobile, U.S. Cellular, Boost Mobile, MetroPCS, Virgin Mobile and
              Cricket. T-Mobile is not liable for delayed or undelivered
              messages.{' '}
            </Text>

            <Text as="p" mb={4}>
              For all questions about the services provided by this short code,
              you can send an email to{' '}
              <Link
                variant="regular"
                color="brandDarkBlue"
                href="mailto:clearcustomersupport@tsa.dhs.gov"
                target="_blank"
              >
                CLEARCustomerSupport@tsa.dhs.gov
              </Link>
              .
            </Text>
            <Text
              as="h2"
              fontSize={4}
              color="brandDarkBlue"
              mb={4}
              id="consent-and-written-release"
            >
              9. Consent and Written Release for Collection, Use, Disclosure and
              Storage of Biometric Information and Identifiers
            </Text>
            <Text as="p" mb={4}>
              As part of enrolling in TSA&nbsp;PreCheck®, your fingerprints and
              photograph will be collected, stored, disclosed and used by us to
              enable your enrollment, and facilitate your application, including
              background checks conducted by TSA and the FBI. Further detail
              regarding the use of your biometric information is set forth above
              in the Introduction to these TSA&nbsp;PreCheck® enrollment Terms
              and Conditions. If you elect to enroll in CLEAR, your biometrics
              may be used to facilitate your enrollment in the CLEAR Program and
              for the other purposes, as set forth in greater detail in CLEAR’s
              privacy policy, available at&nbsp;
              <Link
                variant="regular"
                color="brandDarkBlue"
                href="https://www.clearme.com/privacy-policy"
                target="_blank"
              >
                https://www.clearme.com/privacy-policy.
              </Link>
            </Text>
          </Box>
        </Flex>
      </PageContent>
    </Flex>
  )
}

export default TermsPage
